import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
};

// Products APIs
export const productsAPI = {
  getAll: async (category = null) => {
    const url = category ? `/api/products?category=${category}` : '/api/products';
    const response = await api.get(url);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/products/${id}`);
    return response.data;
  },
  
  create: async (productData) => {
    const response = await api.post('/api/products', productData);
    return response.data;
  },
  
  update: async (id, productData) => {
    const response = await api.put(`/api/products/${id}`, productData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/products/${id}`);
    return response.data;
  },
  
  addFeature: async (productId, feature) => {
    const response = await api.post(`/api/products/${productId}/features`, feature);
    return response.data;
  },
  
  addBenefit: async (productId, benefit) => {
    const response = await api.post(`/api/products/${productId}/benefits`, benefit);
    return response.data;
  },
  
  updateTechnicalSpecs: async (productId, specs) => {
    const response = await api.post(`/api/products/${productId}/technical-specs`, specs);
    return response.data;
  },
};

// Clients APIs
export const clientsAPI = {
  getAll: async () => {
    const response = await api.get('/api/clients');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/clients/${id}`);
    return response.data;
  },
  
  create: async (clientData) => {
    const response = await api.post('/api/clients', clientData);
    return response.data;
  },
  
  update: async (id, clientData) => {
    const response = await api.put(`/api/clients/${id}`, clientData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/clients/${id}`);
    return response.data;
  },
};

// Inquiries APIs
export const inquiriesAPI = {
  getAll: async (status = null) => {
    const url = status ? `/api/inquiries?status=${status}` : '/api/inquiries';
    const response = await api.get(url);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/inquiries/${id}`);
    return response.data;
  },
  
  create: async (inquiryData) => {
    const response = await api.post('/api/inquiries', inquiryData);
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await api.patch(`/api/inquiries/${id}`, { status });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/inquiries/${id}`);
    return response.data;
  },
};

// Settings APIs
export const settingsAPI = {
  getAll: async () => {
    const response = await api.get('/api/settings');
    return response.data;
  },
  
  getByKey: async (key) => {
    const response = await api.get(`/api/settings/${key}`);
    return response.data;
  },
  
  update: async (settings) => {
    // If settings is an object with multiple keys, use bulk update
    if (typeof settings === 'object' && !settings.value) {
      const response = await api.put('/api/settings', settings);
      return response.data;
    }
    // Otherwise, assume it's a single setting update (legacy)
    const response = await api.put(`/api/settings/${settings.key}`, { value: settings.value });
    return response.data;
  },
  
  updateSingle: async (key, value) => {
    const response = await api.put(`/api/settings/${key}`, { value });
    return response.data;
  },
  
  create: async (key, value) => {
    const response = await api.post('/api/settings', { key, value });
    return response.data;
  },
};

// Solutions APIs
export const solutionsAPI = {
  getAll: async () => {
    const response = await api.get('/api/solutions');
    return response.data;
  },
  
  get: async (id) => {
    const response = await api.get(`/api/solutions/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/api/solutions', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/solutions/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/solutions/${id}`);
    return response.data;
  },
};

// Capabilities APIs
export const capabilitiesAPI = {
  getAll: async () => {
    const response = await api.get('/api/capabilities');
    return response.data;
  },
  
  get: async (id) => {
    const response = await api.get(`/api/capabilities/${id}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/api/capabilities', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/capabilities/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/capabilities/${id}`);
    return response.data;
  },
};

// Users APIs
export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/api/users');
    return response.data;
  },
  
  create: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response.data;
  },
  
  update: async (id, userData) => {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  },
};

// Upload APIs
export const uploadAPI = {
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  uploadBrochure: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/upload/brochure', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/upload/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  deleteFile: async (filePath) => {
    const response = await api.delete(`/api/upload/${filePath}`);
    return response.data;
  },
};

export default api;
